import {Component, OnInit} from '@angular/core';
import {FormControl, FormGroup, ReactiveFormsModule, ValidationErrors, Validators} from "@angular/forms";
import {InputTextModule} from "primeng/inputtext";
import {TextareaModule} from "primeng/textarea";
import {CustomDialogData} from "../../../core/models/custom-dialog.model";
import {DialogService, DynamicDialog, DynamicDialogRef} from "primeng/dynamicdialog";
import {IPermission} from "../../../core/models/permission.model";
import {IInstitution} from "../../../core/models/institution.model";
import {GroupService} from "../../../core/services/group.service";
import {ToastService} from "../../../core/services/toast.service";
import {AccordionModule} from "primeng/accordion";
import {UserService} from "../../../core/services/user.service";
import {getDefaultPageable, Pageable, PageParams} from "../../../core/models/page.model";
import {IUser} from "../../../core/models/user.model";
import {DynamicTableComponent} from "../../../shared/components/dynamic-table/dynamic-table.component";
import {PaginatorState} from "primeng/paginator";
import {DEFAULT_ROWS_PER_PAGE_OPTIONS} from "../../../core/utils/utils";
import {CheckboxModule} from "primeng/checkbox";
import {NgClass, NgIf} from "@angular/common";
import {GroupPermissionsComponent} from "../group-permissions/group-permissions.component";
import {CustomTableHeader} from "../../../core/models/custom-table.model";

@Component({
    selector: 'app-group-details',
    imports: [
        // Modules
        ReactiveFormsModule, InputTextModule, TextareaModule, AccordionModule, CheckboxModule,
        // Components
        DynamicTableComponent, GroupPermissionsComponent,
        // Directives & Pipes
        NgIf, NgClass
    ],
    templateUrl: './group-details.component.html',
    styleUrl: './group-details.component.scss'
})
export class GroupDetailsComponent implements OnInit {

  formGroup = new FormGroup({
    name: new FormControl('', {
      nonNullable: true,
      validators: [Validators.required, Validators.minLength(3), Validators.maxLength(100)]
    }),
    institution: new FormControl<IInstitution>({} as IInstitution, {
      nonNullable: true,
      validators: [Validators.required]
    }),
    description: new FormControl('', {
      nonNullable: true,
      validators: [Validators.required, Validators.minLength(10), Validators.maxLength(500)]
    }),
    permissions: new FormControl<IPermission[]>([], {
      nonNullable: true,
      validators: [Validators.required, Validators.minLength(1)]
    })
  });
  users: IUser[] = [];
  boundData: CustomDialogData | undefined;
  instance: DynamicDialog | undefined;
  isCreate: boolean = false;
  userCols: CustomTableHeader[] = [
    {
      key: 'lastname',
      column: 'Nom',
      type: 'text'
    },
    {
      key: 'firstname',
      column: 'Prénom',
      type: 'text'
    },
    {
      key: 'enabled',
      column: 'Status',
      type: 'boolean'
    }
  ];
  pageable: PageParams = {page: 0, first: 0, rows: 0, totalRecords: 0, rowPerPageOptions: []};
  permissions: IPermission[] = [];


  constructor(private groupService: GroupService, private dialogService: DialogService, private userService: UserService,
              private toastService: ToastService, private ref: DynamicDialogRef) {
    this.instance = this.dialogService.getInstance(this.ref);
  }

  ngOnInit() {
    this.boundData = this.instance?.data;
    this.permissions = this.boundData?.data.permissions || [];
    this.loadUsersForGroup(getDefaultPageable());
    if (this.boundData?.isCreate) this.isCreate = true;
    if ((this.boundData?.isEdit || this.boundData?.isView) && this.boundData.data) this.formGroup.patchValue(this.boundData.data);
    if (this.boundData?.isView) this.formGroup.disable();
  }

  loadUsersForGroup(pageable: Pageable = getDefaultPageable(), ids: string[] = [this.boundData?.data.id]) {
    this.userService.getUsersByGroup(ids, pageable).subscribe({
      next: page => {
        this.users = page.content;
        this.pageable = {
          page: page.number,
          first: page.number * page.size,
          rows: page.size,
          totalRecords: page.totalElements,
          rowPerPageOptions: DEFAULT_ROWS_PER_PAGE_OPTIONS
        }
      },
      error: () => {
        this.toastService.showToast('Erreur lors du chargement des utilisateurs', 'Une erreur est survenue lors du chargement des utilisateurs.', 'error');
      }
    })

  }

  /**
   * Récupère le message d'erreur pour un champ donné
   */
  getFieldError(fieldName: string): string {
    const control = this.formGroup.get(fieldName);
    if (!control || !control.errors || !control.touched) {
      return '';
    }

    const errors = control.errors;
    if (errors['required']) return 'Ce champ est requis';
    if (errors['minlength']) return `Minimum ${errors['minlength'].requiredLength} caractères`;
    if (errors['maxlength']) return `Maximum ${errors['maxlength'].requiredLength} caractères`;

    return 'Valeur invalide';
  }

  /**
   * Vérifie si un champ a une erreur et a été touché
   */
  hasFieldError(fieldName: string): boolean {
    const control = this.formGroup.get(fieldName);
    return !!(control && control.errors && control.touched);
  }

  /**
   * Marque tous les champs comme touchés pour afficher les erreurs
   */
  private markAllFieldsAsTouched(): void {
    Object.keys(this.formGroup.controls).forEach(key => {
      const control = this.formGroup.get(key);
      control?.markAsTouched();
    });
  }

  /**
   * Gère les erreurs HTTP et affiche un message approprié
   */
  private handleHttpError(error: any, operation: string): void {
    let errorMessage = 'Une erreur est survenue. Veuillez réessayer.';

    if (error.error) {
      if (typeof error.error === 'string') {
        errorMessage = error.error;
      } else if (error.error.message) {
        errorMessage = error.error.message;
      } else if (error.error.error) {
        errorMessage = error.error.error;
      }
    } else if (error.message) {
      errorMessage = error.message;
    }

    // Messages d'erreur spécifiques selon le code HTTP
    if (error.status === 400) {
      errorMessage = 'Données invalides. ' + errorMessage;
    } else if (error.status === 401) {
      errorMessage = 'Non autorisé. Veuillez vous reconnecter.';
    } else if (error.status === 403) {
      errorMessage = 'Accès refusé. Vous n\'avez pas les permissions nécessaires.';
    } else if (error.status === 404) {
      errorMessage = 'Groupe non trouvé.';
    } else if (error.status === 409) {
      errorMessage = 'Conflit. Un groupe avec ce nom existe déjà.';
    } else if (error.status === 500) {
      errorMessage = 'Erreur serveur. Veuillez contacter l\'administrateur.';
    } else if (error.status === 0) {
      errorMessage = 'Impossible de contacter le serveur. Vérifiez votre connexion.';
    }

    this.toastService.showToast(
      `Erreur lors de la ${operation}`,
      errorMessage,
      'error'
    );
  }

  toggleGroup() {
    const enabled = this.boundData?.data.enabled;
    this.groupService.toggleGroup(this.boundData?.data.id).subscribe({
      next: () => {
        this.toastService.showToast(enabled ? 'Groupe désactivé' : 'Groupe activé', `Le groupe a été ${enabled ? 'désactivé' : 'activé'} avec succès.`);
        this.ref.close();
      },
      error: (err) => {
        const action = enabled ? 'désactivation' : 'activation';
        this.handleHttpError(err, action);
      }
    });
  }

  onPageChange = (event: PaginatorState) => this.loadUsersForGroup(getDefaultPageable(event.page, event.rows));

  permissionChange($event: IPermission[]) {
  }
}
